import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  useFlag,
  LoadingPage,
  EmbeddedErrorPage,
  RouteParams,
  RouteProps,
} from '@sketch/modules-common'

// External components
import { Box, EditableText } from '@sketch/components'
import EditorLayout from '../../layouts/EditorLayout'
import { DsmTextEditor } from '../../components'

// Styles
import {
  PageWrapper,
  PageContent,
  PageSeparator as Separator,
  Title,
  Description,
} from './DocPage.styles'

// Utils
import { DesignSystem as DesignSystemType } from '../../types'

// Hooks
import { PrivateWorkspaceRouteExtraProps } from 'modules/workspace/containers'
import {
  useGetDesignSystemQuery,
  useGetDesignSystemSectionsQuery,
} from '@sketch/gql-types'

// Constants
const defaultEditorContent = {
  content: [
    {
      attrs: {
        level: 1,
      },
      content: [
        {
          text: 'Text editor playground',
          type: 'text',
        },
      ],
      type: 'heading',
    },
    {
      content: [
        {
          text:
            'This is a description for the design system page, you can just click and start writing...',
          type: 'text',
        },
      ],
      type: 'paragraph',
    },
  ],
  type: 'doc',
}

export interface DocPageProps
  extends RouteProps<'DESIGN_SYSTEM_PAGES'>,
    PrivateWorkspaceRouteExtraProps {}

export const DocPage: React.FC<DocPageProps> = ({ workspace }) => {
  const isDesignSystemsEditorOn = useFlag('design-systems-editor')
  const { designSystemId, pageId } = useParams<
    RouteParams<'DESIGN_SYSTEM_PAGES'>
  >()
  const [title, setTitle] = useState(pageId === 'new' ? '' : 'A title')
  const [description, setDescription] = useState(
    pageId === 'new' ? '' : 'A description'
  )

  const { data, loading, error } = useGetDesignSystemQuery({
    variables: { identifier: designSystemId },
  })

  const designSystem = data?.designSystem

  const {
    data: sectionsData,
    loading: sectionsLoading,
    error: sectionsError,
  } = useGetDesignSystemSectionsQuery({
    variables: {
      identifier: designSystem?.identifier || '',
    },
    skip: !designSystem?.identifier,
  })

  if (loading || sectionsLoading) {
    return <LoadingPage.WorkspaceSidebar />
  }

  if (error || sectionsError || !isDesignSystemsEditorOn) {
    return <EmbeddedErrorPage for="unknown" />
  }

  const sections = sectionsData?.designSystem?.sections || []

  return (
    <EditorLayout
      designSystem={designSystem as DesignSystemType}
      sections={sections}
      workspace={workspace}
    >
      <PageWrapper>
        <PageContent>
          <Box mb={4}>
            <EditableText
              value={title}
              placeholder="New page title..."
              onEdit={setTitle}
              element={Title}
              noExtraStyling
            />
          </Box>
          <EditableText
            value={description}
            placeholder="New page description..."
            onEdit={setDescription}
            element={Description}
            noExtraStyling
            removePlaceholderMarginBottom
          />
          <Separator />
          <DsmTextEditor
            content={pageId === 'new' ? {} : defaultEditorContent}
          />
        </PageContent>
      </PageWrapper>
    </EditorLayout>
  )
}
